import React from "react";
import { AllMethodologiesResult } from "../types";
import { Download, Printer, Scale } from "lucide-react";
import AdviceBlock from "./AdviceBlock";

interface Props {
  data: AllMethodologiesResult;
  onExportCsv?: () => void;
}

export default function CompareTab({ data, onExportCsv }: Props) {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Top Banner with controls */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100/80 shadow-sm/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all duration-300 hover:shadow-md hover:border-slate-200/50">
        <div className="space-y-1">
          <h3 className="font-extrabold text-lg font-display text-slate-900 flex items-center gap-2">
            <Scale className="w-5 h-5 text-indigo-600 shrink-0" />
            Сводный аналитический отчет
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Финальный срез устойчивости заемщика. Сравнение методологий и экспорт готовых данных для заключения службы экономической безопасности.
          </p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto shrink-0">
          {onExportCsv && (
          <button
            onClick={onExportCsv}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4.5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md shadow-indigo-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <Download className="w-4 h-4" />
            Экспорт (Excel)
          </button>
          )}
          <button 
            onClick={handlePrint}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4.5 py-2.5 bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 duration-200 cursor-pointer"
          >
            <Printer className="w-4 h-4" />
            Печать (PDF)
          </button>
        </div>
      </div>

      {/* Side-by-side 4 Methodologies comparative grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Met 1: ANYskin */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm/5 space-y-3 hover:shadow-md hover:border-indigo-100 hover:-translate-y-1 transition-all duration-300 group">
          <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-wider">Методика 1</span>
          <h4 className="font-extrabold font-display text-slate-900 text-sm group-hover:text-indigo-650 transition-colors">ANYskin Рейтинговая</h4>
          <div className="py-2 flex items-baseline gap-1.5">
            <span className="text-4xl font-black font-display text-slate-900 tracking-tight">{data.anyskin.rating}</span>
            <span className="text-[10px] font-mono tracking-widest text-slate-400 font-bold uppercase">класс</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Взвешенная оценка: <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50/50 px-1.5 py-0.5 rounded">{data.anyskin.totalScore}б</span>. Учет опыта контрактов и 12 стоп-факторов риска.
          </p>
        </div>

        {/* Met 2: Creditability */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm/5 space-y-3 hover:shadow-md hover:border-indigo-100 hover:-translate-y-1 transition-all duration-300 group">
          <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-wider">Методика 2</span>
          <h4 className="font-extrabold font-display text-slate-900 text-sm group-hover:text-indigo-650 transition-colors">Кредитоспособность</h4>
          <div className="py-2 flex items-baseline gap-1.5">
            <span className="text-4xl font-black font-display text-indigo-600 tracking-tight">{data.credit.fScore}</span>
            <span className="text-[10px] font-mono tracking-widest text-slate-400 font-bold uppercase">индекс F</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Категория риска: <span className="font-sans text-xs font-bold text-slate-800 bg-slate-100/70 px-1.5 py-0.5 rounded">{data.credit.riskCategory}</span>. Тест чистых активов, оборотности и доходов компании.
          </p>
        </div>

        {/* Met 3: Solvency */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm/5 space-y-3 hover:shadow-md hover:border-indigo-100 hover:-translate-y-1 transition-all duration-300 group">
          <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-wider">Методика 3</span>
          <h4 className="font-extrabold font-display text-slate-900 text-sm group-hover:text-indigo-650 transition-colors">Платежеспособность</h4>
          <div className="py-2 flex items-baseline gap-1.5">
            <span className="text-3xl font-black font-display text-slate-900 tracking-tight">
              {data.solvency.details.l1.status === "ok" ? "Устойч." : "Дефицит"}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Прогноз 6 мес: <span className={`font-mono text-xs font-bold px-1.5 py-0.5 rounded ${data.solvency.l8 >= 1.0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>Л8={data.solvency.l8}</span>. Оценка запаса восстановления ресурсов ликвидности.
          </p>
        </div>

        {/* Met 4: Points & Sber */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm/5 space-y-3 hover:shadow-md hover:border-indigo-100 hover:-translate-y-1 transition-all duration-300 group">
          <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg uppercase tracking-wider">Методика 4</span>
          <h4 className="font-extrabold font-display text-slate-900 text-sm group-hover:text-indigo-650 transition-colors">Потенциал кредита</h4>
          <div className="py-2 flex items-baseline gap-1.5">
            <span className="text-4xl font-black font-display text-slate-900 tracking-tight">{data.score.totalPoints}</span>
            <span className="text-[10px] font-mono tracking-widest text-slate-400 font-bold uppercase">/ 100б</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Группа TOP банка: <span className="font-sans text-xs font-bold text-slate-800 bg-slate-100/70 px-1.5 py-0.5 rounded">{data.score.sberbankClass}</span>. Классификация по балльной сетке риска РФ.
          </p>
        </div>
      </div>

      {/* Comparative Detailed Indicator Table Matrix */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-slate-200/40">
        <div className="p-5 border-b border-slate-100 bg-slate-50/50">
          <h4 className="font-extrabold font-display text-slate-900 text-sm">Кросс-методологическая матрица показателей контрагента</h4>
        </div>
        {/* Матрица сравнения: на узких экранах горизонтальный скролл с залипшей
            первой колонкой «Показатель» — суть таблицы в сопоставлении значений по строке. */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100 text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
                <th className="py-3.5 px-3 md:px-6 sticky left-0 z-10 bg-slate-50">Показатель</th>
                <th className="py-3.5 px-2 md:px-4 text-center">Код</th>
                <th className="py-3.5 px-2 md:px-4 text-center">Норматив / Описание</th>
                <th className="py-3.5 px-2 md:px-4 text-center">ANYskin Рейтинговый</th>
                <th className="py-3.5 px-2 md:px-4 text-center">Кредитоспособность</th>
                <th className="py-3.5 px-2 md:px-4 text-center">Платежеспособность</th>
                <th className="py-3.5 px-2 md:px-4 text-center">Потенциал кредита</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700 font-sans">
              {/* Row 1: Autonomy */}
              <tr className="hover:bg-indigo-50/10 transition-colors">
                <td className="py-4.5 px-3 md:px-6 font-semibold text-slate-900 text-sm sticky left-0 z-10 bg-white">Коэффициент финансовой автономии</td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono font-extrabold text-xs text-indigo-700">
                  <span className="bg-indigo-50 px-2 py-1 rounded-md">К1 / У1</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono opacity-80 leading-snug">
                  <span className="text-slate-650 font-semibold">&gt; 0.5</span>
                  <span className="block text-[9.5px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">высокая финансовая устойчивость</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-bold text-slate-950">Q1: {data.anyskin.details.Q1.score}</span> <span className="text-[10px] text-slate-400 font-semibold">баллов</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.credit.k1}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">{data.credit.details.K1.group} risk</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.solvency.l7}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">Л7 (СОС)</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-extrabold text-indigo-650">{data.score.details.U1.points}</span> <span className="text-[10px] text-slate-450">/ 15 баллов</span>
                </td>
              </tr>

              {/* Row 2: Liquidity */}
              <tr className="hover:bg-indigo-50/10 transition-colors">
                <td className="py-4.5 px-3 md:px-6 font-semibold text-slate-900 text-sm sticky left-0 z-10 bg-white">Коэффициент текущей ликвидности</td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono font-extrabold text-xs text-indigo-700">
                  <span className="bg-indigo-50 px-2 py-1 rounded-md">Q3 / К4 / Л4</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono opacity-80 leading-snug">
                  <span className="text-slate-650 font-semibold">&gt; 2.0</span>
                  <span className="block text-[9.5px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">полная краткосрочная покрываемость</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-bold text-slate-950">Q3: {data.anyskin.details.Q3.score}</span> <span className="text-[10px] text-slate-400 font-semibold">баллов</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.credit.k4}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">{data.credit.details.K4.group} risk</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.solvency.l4}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">Л4 (Ликвидно)</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-extrabold text-indigo-650">{data.score.details.L4.points}</span> <span className="text-[10px] text-slate-450">/ 16.5 баллов</span>
                </td>
              </tr>

              {/* Row 3: Absolute Liquidity */}
              <tr className="hover:bg-indigo-50/10 transition-colors">
                <td className="py-4.5 px-3 md:px-6 font-semibold text-slate-900 text-sm sticky left-0 z-10 bg-white">Коэффициент абсолютной ликвидности</td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono font-extrabold text-xs text-indigo-700">
                  <span className="bg-indigo-50 px-2 py-1 rounded-md">К5 / Л2</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono opacity-80 leading-snug">
                  <span className="text-slate-650 font-semibold">0.2 - 0.25</span>
                  <span className="block text-[9.5px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">погашение задолженности кэшем</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono text-slate-400 font-semibold italic">нет у TOP банка</td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.credit.k5}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">{data.credit.details.K5.group} risk</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.solvency.l2}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">Л2 (Абс)</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-extrabold text-indigo-650">{data.score.details.L2.points}</span> <span className="text-[10px] text-slate-450">/ 20 баллов</span>
                </td>
              </tr>

              {/* Row 4: ROA */}
              <tr className="hover:bg-indigo-50/10 transition-colors">
                <td className="py-4.5 px-3 md:px-6 font-semibold text-slate-900 text-sm sticky left-0 z-10 bg-white">Операционная рентабельность (ROA / EBITDA)</td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono font-extrabold text-xs text-indigo-700">
                  <span className="bg-indigo-50 px-2 py-1 rounded-md">Q5 / К6</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono opacity-80 leading-snug">
                  <span className="text-slate-650 font-semibold">&gt; 10%</span>
                  <span className="block text-[9.5px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">здоровый операционный поток</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono">
                  <span className="font-bold text-slate-950">Q5: {data.anyskin.details.Q5.score}</span> <span className="text-[10px] text-slate-400 font-semibold">баллов</span>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center">
                  <div className="flex flex-col items-center">
                    <span className="font-mono font-black text-slate-900 text-sm">{data.credit.k6}</span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100/55 mt-1">{data.credit.details.K6.group} risk</span>
                  </div>
                </td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono text-slate-400 font-semibold italic">—</td>
                <td className="py-4.5 px-2 md:px-4 text-center font-mono text-slate-400 font-semibold italic">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {data.recommendations && data.recommendations.length > 0 && (
        <AdviceBlock title="Что улучшить — сводный план действий" recommendations={data.recommendations} />
      )}
    </div>
  );
}

